import { useState } from 'react';

import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type { VehicleTemplate } from '@klareospace/pharos-lib-types';

import { useVehicleTemplates } from './use-vehicle-templates';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseVehicleTemplatesPageOptions {
  enabled?: boolean;
}

export interface UseVehicleTemplatesPageReturn {
  error: Error | null;
  isLoading: boolean;
  onProfileChange: (values: string[]) => void;
  onSearchChange: (value: string) => void;
  onStatusChange: (values: string[]) => void;
  profileFilter: string[];
  searchValue: string;
  statusFilter: string[];
  vehicleTemplates: VehicleTemplate[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useVehicleTemplatesPage(options?: UseVehicleTemplatesPageOptions): UseVehicleTemplatesPageReturn {
  const searchState = useSearchPageState();
  const [statusFilter, setStatusFilter] = useState<string[]>(['active']);
  const [profileFilter, setProfileFilter] = useState<string[]>([]);

  const { data, error, isLoading } = useVehicleTemplates({
    enabled: options?.enabled,
    profiles: profileFilter.length > 0 ? profileFilter : undefined,
    search: searchState.debouncedSearch,
    statuses: statusFilter.length > 0 ? statusFilter : undefined,
  });

  function handleStatusChange(values: string[]): void {
    setStatusFilter(values);
    searchState.resetPage();
  }

  function handleProfileChange(values: string[]): void {
    setProfileFilter(values);
    searchState.resetPage();
  }

  return {
    error: error ?? null,
    isLoading,
    onProfileChange: handleProfileChange,
    onSearchChange: searchState.onSearchChange,
    onStatusChange: handleStatusChange,
    profileFilter,
    searchValue: searchState.searchValue,
    statusFilter,
    vehicleTemplates: data?.items ?? [],
  };
}
